import {HeightTransition} from '@aboutyou/transitions';
import {isElementFullyInViewport} from '@aboutyou/utils';
import debounce from 'lodash.debounce';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import styled, {css} from 'styled-components';
import {COLORS} from '../../../../constants/colors';
import {tabletAndUp} from '../../../../constants/customMedia';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../../constants/typography';
import {UnstyledButton} from '../../../atoms/Buttons/UnstyledButton';
import {DefaultToggleIcon} from '../../Accordion/DefaultToggleIcon';

interface ToggleIconProps {
  isOpen: boolean;
}

interface FilterAccordionProps {
  id?: string;
  withContentTeaser?: boolean;
  toggleContent?: string | React.ReactElement;
  content: string | React.ReactElement;
  className?: string;
  scrollTitleInView?: boolean;
  onOpen?: VoidFunction;
  onClose?: VoidFunction;
  trackClose?: VoidFunction;
  trackOpen?: VoidFunction;
  'data-testid'?: string;
  ToggleIcon?: React.ComponentType<ToggleIconProps>;
  subTitle?: string;
  withoutBottomBorder?: boolean;
}

export const FilterAccordion: React.FC<FilterAccordionProps> = ({
  id,
  withContentTeaser,
  toggleContent,
  content,
  className,
  scrollTitleInView,
  onOpen,
  onClose,
  trackClose,
  trackOpen,
  'data-testid': dataTestid = 'accordionContainer',
  ToggleIcon = DefaultToggleIcon,
  subTitle,
  withoutBottomBorder,
}) => {
  const [toggleActive, setToggle] = useState(false);
  const [height, setHeight] = useState(0);
  const [toggleContentHeight, setToggleContentHeight] = useState(0);
  const toggleContentRef = useRef<HTMLDivElement | null>(null);
  const openerButtonRef = useRef<HTMLButtonElement | null>(null);
  const contentContainerRef = useRef<HTMLDivElement | null>(null);
  const accordionContentContainerId = useMemo(
    () => `accordionContentContainer${id ? '-' + id : ''}`,
    [id],
  );

  const measuredRef = useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      toggleContentRef.current = node;
      setToggleContentHeight(node.clientHeight);
    }
  }, []);

  const onClickHandler = () => {
    toggleActive ? trackClose?.() : trackOpen?.();
    setToggle(!toggleActive);
  };

  // The onOpenHandler function is called when an accordion is opened.
  // If the scrollTitleInView prop is true, it adds a 'transitionend' event listener
  // to the ContentContainer. When the transition ends, it checks if the accordion
  // title is fully in the viewport. If not, it scrolls the title into view smoothly.
  // Finally, it removes the event listener to prevent memory leaks.
  const onOpenHandler = useCallback(() => {
    onOpen?.();

    if (!scrollTitleInView) {
      return;
    }

    const debouncedScrollIntoView = debounce(() => {
      openerButtonRef.current?.scrollIntoView({behavior: 'smooth'});
    }, 200);

    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.propertyName === 'max-height') {
        if (!isElementFullyInViewport(openerButtonRef.current as HTMLElement)) {
          debouncedScrollIntoView();
        }
        contentContainerRef.current?.removeEventListener(
          'transitionend',
          handleTransitionEnd,
        );
      }
    };
    contentContainerRef.current?.addEventListener(
      'transitionend',
      handleTransitionEnd,
    );
  }, [onOpen, scrollTitleInView]);

  useEffect(() => {
    if (toggleActive) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
      setHeight(toggleContentHeight);
      onOpenHandler();
    } else if (!toggleActive && withContentTeaser) {
      setHeight(60);
      onClose?.();
    } else {
      setHeight(0);
      onClose?.();
    }
  }, [
    toggleContentHeight,
    toggleActive,
    withContentTeaser,
    onClose,
    onOpenHandler,
  ]);

  return (
    <Wrapper
      data-testid={dataTestid}
      aria-expanded={toggleActive}
      className={className}
      $withoutBottomBorder={withoutBottomBorder}
    >
      <TitleContainer
        aria-controls={accordionContentContainerId}
        aria-pressed="false"
        onClick={onClickHandler}
        data-testid="accordionTitleContainer"
        ref={openerButtonRef}
        aria-label={undefined}
      >
        <Title>{content}</Title>
        <SubTitleWrapper>
          <SubTitle>{subTitle}</SubTitle>
          <ToggleIcon isOpen={Boolean(toggleActive && toggleContent)} />
        </SubTitleWrapper>
      </TitleContainer>
      <HeightTransition mounted={toggleActive} duration={120}>
        <ContentContainer
          id={accordionContentContainerId}
          $height={height}
          data-testid="accordionContent"
          aria-hidden={!toggleActive}
          ref={contentContainerRef}
        >
          <div ref={measuredRef}>{toggleContent}</div>
        </ContentContainer>
      </HeightTransition>
    </Wrapper>
  );
};

const Wrapper = styled.div<{$withoutBottomBorder?: boolean}>`
  background-color: ${COLORS.White};
  border-bottom: none;
  -webkit-tap-highlight-color: transparent;

  &:last-of-type {
    ${props =>
      props.$withoutBottomBorder
        ? ''
        : `border-bottom: solid 1px ${COLORS.Grey4};`}
  }

  &:hover {
    cursor: pointer;
  }

  ${tabletAndUp(css`
    &:first-of-type {
      border-radius: 5px 5px 0px 0px;
    }

    &:last-of-type {
      border-radius: 0px 0px 5px 5px;
      border-bottom: solid 1px ${COLORS.Grey4};
    }
  `)}
`;

const TitleContainer = styled(UnstyledButton)`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  text-align: left;

  &&& {
    padding: 1.5rem 0;
  }

  & > :last-child {
    margin-left: auto;
  }
`;

const ContentContainer = styled.div<{$height: number}>`
  max-height: ${props => (props.$height ? '60px' : 0)};
  margin-bottom: ${props => (props.$height ? '1.2rem' : 0)};
  overflow: hidden;
`;

const SubTitle = styled.div`
  color: ${COLORS.Grey1};
  font-size: ${FONT_SIZES.px10};
  display: flex;
  align-items: center;
  text-transform: capitalize;
`;

const Title = styled.h4`
  font-weight: ${FONT_WEIGHTS.mediumBold};
  font-size: ${FONT_SIZES.px14};
  color: ${COLORS.Black};
  margin: 0 4px 0 0;
`;

const SubTitleWrapper = styled.div`
  display: flex;
  gap: 8px;
`;
